<template>
  <v-container fluid class="pa-4">
    <!-- Header -->
    <v-row class="mb-2 align-center justify-space-between">
      <v-col cols="12" md="6" class="d-flex align-center">
        <v-icon color="deep-purple darken-2" large class="mr-3">mdi-cards-outline</v-icon>
        <div>
          <h2 class="headline font-weight-bold deep-purple--text text--darken-3">Estimación por Tallas y Consenso Técnico</h2>
          <div class="caption text-uppercase grey--text text--darken-1">Votaciones por Historia de Usuario (Planning Poker)</div>
        </div>
      </v-col>
      <v-col cols="12" md="6" class="text-right d-flex align-center justify-end">
        <v-btn color="deep-purple darken-2" dark class="elevation-2" @click="loadData">
          <v-icon left>mdi-refresh</v-icon> Actualizar
        </v-btn>
      </v-col>
    </v-row>

    <!-- Filtros -->
    <v-card outlined class="pa-3 mb-4 rounded-lg grey lighten-5 elevation-1">
      <v-row dense class="align-center">
        <v-col cols="12" sm="4">
          <s-text label="Título del Requerimiento" v-model="filter.ReqTitle" clearable @keyup.enter="loadData"></s-text>
        </v-col>
        <v-col cols="12" sm="4">
          <s-select-definition label="Tipo de Requerimiento" :def="1720" v-model="filter.ReqType" clearable></s-select-definition>
        </v-col>
        <v-col cols="12" sm="4" class="d-flex align-center pt-4">
          <v-btn small color="deep-purple darken-2" dark @click="loadData">
            <v-icon small left>mdi-magnify</v-icon> Buscar
          </v-btn>
        </v-col>
      </v-row>
    </v-card>

    <!-- Agrupación: Requerimiento -> Historias de Usuario -->
    <div v-if="loading" class="pa-8 text-center">
      <v-progress-circular indeterminate color="deep-purple darken-2" size="48"></v-progress-circular>
      <div class="caption grey--text mt-2">Cargando requerimientos e historias...</div>
    </div>

    <v-alert v-else-if="groups.length === 0" type="info" text class="mb-4">
      No hay requerimientos en estado 'En Refinamiento' con historias para estimar.
    </v-alert>

    <v-expansion-panels v-else multiple class="mb-4">
      <v-expansion-panel v-for="group in groups" :key="group.req.ReqID" class="mb-2 rounded-lg">
        <v-expansion-panel-header class="grey lighten-4">
          <div class="d-flex align-center flex-wrap" style="width: 100%">
            <span class="caption font-weight-bold primary--text mr-3">{{ group.req.ReqCode }}</span>
            <span class="body-2 font-weight-bold text--primary mr-3">{{ group.req.ReqTitle }}</span>
            <v-chip v-if="group.req.ReqProject" x-small color="teal lighten-4" class="teal--text font-weight-bold mr-2">
              {{ group.req.ReqProject }}
            </v-chip>
            <v-chip v-if="group.req.ReqInitialScore != null" x-small :color="getScoreColor(group.req.ReqInitialScore)" dark class="font-weight-bold">
              {{ group.req.ReqInitialScore }} pts
            </v-chip>
            <v-spacer></v-spacer>
            <v-chip x-small color="deep-purple darken-1" dark class="font-weight-bold mr-1">
              {{ group.stories.length }} historia(s)
            </v-chip>
          </div>
        </v-expansion-panel-header>

        <v-expansion-panel-content>
          <v-container fluid class="pa-2">
            <v-row>
              <v-col v-for="story in group.stories" :key="story.UsoID" cols="12" md="6">
                <v-card outlined class="rounded-lg elevation-1 mb-3">
                  <div class="pa-3">
                    <!-- Encabezado de la historia -->
                    <div class="d-flex align-center justify-space-between mb-2">
                      <div class="d-flex align-center flex-wrap">
                        <span class="caption font-weight-bold deep-purple--text text--darken-2 mr-2">{{ story.UsoCode }}</span>
                        <v-chip x-small :color="statusColor(story.UsoStatus)" dark class="font-weight-bold mr-2">
                          {{ story.UsoStatusName || 'En Estimación' }}
                        </v-chip>
                        <v-chip v-if="story.UsoConsensusConfirmed" x-small color="green" dark class="font-weight-bold mr-2">
                          <v-icon x-small left>mdi-check-decagram</v-icon> Consenso
                        </v-chip>
                        <v-chip v-if="story.UsoFinalSizeName" x-small color="indigo lighten-4" class="indigo--text font-weight-bold">
                          {{ story.UsoFinalSizeName }} · {{ story.UsoStoryPoints }} pts
                        </v-chip>
                      </div>
                      <div class="d-flex align-center">
                        <v-btn icon small color="warning darken-2" :disabled="story.UsoConsensusConfirmed" @click="openSplitDialog(story)" title="Dividir Historia">
                          <v-icon small>mdi-call-split</v-icon>
                        </v-btn>
                        <v-btn small color="green darken-1" dark :disabled="story.UsoConsensusConfirmed" :loading="closingVoting === story.UsoID" @click="closeVoting(story)">
                          <v-icon small left>mdi-lock-check</v-icon> Cerrar Votación
                        </v-btn>
                      </div>
                    </div>

                    <!-- Título de la historia -->
                    <div class="body-2 font-weight-bold text--primary mb-2">{{ story.UsoTitle }}</div>

                    <!-- Criterios de aceptación (truncados para no desbordar la tabla) -->
                    <div v-if="story.UsoAcceptanceCriteria" class="mb-2">
                      <div class="d-flex align-center">
                        <span class="text-caption font-weight-bold text-uppercase grey--text text--darken-2 mr-2">Criterios de Aceptación</span>
                        <v-btn x-small text color="primary" @click="toggleCriteria(story)">
                          {{ expandedCriteria[story.UsoID] ? 'Ver menos' : 'Ver más' }}
                        </v-btn>
                      </div>
                      <div
                        class="body-2 grey--text text--darken-2"
                        :class="expandedCriteria[story.UsoID] ? '' : 'line-clamp-2'"
                        style="white-space: pre-line;"
                      >
                        {{ story.UsoAcceptanceCriteria }}
                      </div>
                    </div>

                    <!-- AS-IS / TO-BE resumidos -->
                    <v-row dense class="mb-2" v-if="story.UsoAsIs || story.UsoToBe">
                      <v-col cols="12" sm="6" v-if="story.UsoAsIs">
                        <div class="text-caption font-weight-bold grey--text text--darken-2">AS-IS</div>
                        <div class="body-2 grey--text text--darken-2 line-clamp-2">{{ story.UsoAsIs }}</div>
                      </v-col>
                      <v-col cols="12" sm="6" v-if="story.UsoToBe">
                        <div class="text-caption font-weight-bold grey--text text--darken-2">TO-BE</div>
                        <div class="body-2 grey--text text--darken-2 line-clamp-2">{{ story.UsoToBe }}</div>
                      </v-col>
                    </v-row>

                    <!-- Votos registrados -->
                    <v-card outlined class="pa-2 mb-2 blue-grey lighten-5 rounded-lg">
                      <div class="d-flex align-center justify-space-between mb-1">
                        <span class="font-weight-bold text-caption text-uppercase grey--text text--darken-3">Votaciones de Planning Poker</span>
                        <v-btn x-small color="indigo" outlined class="px-1" @click="loadVotes(story)">
                          <v-icon x-small left>mdi-refresh</v-icon> Actualizar Votos
                        </v-btn>
                      </div>
                      <v-data-table
                        :headers="voteHeaders"
                        :items="votesByStory[story.UsoID] || []"
                        hide-default-footer
                        dense
                        class="elevation-0 transparent"
                        :no-data-text="'Aún no hay votos registrados para esta historia'"
                      >
                        <template v-slot:item.VoteSizeName="{ item }">
                          <v-chip small color="deep-purple darken-1" dark class="font-weight-bold">
                            {{ item.VoteSizeName || 'Talla ' + item.VoteSize }}
                          </v-chip>
                        </template>
                      </v-data-table>

                      <!-- Resumen del consenso actual -->
                      <div v-if="(votesByStory[story.UsoID] || []).length > 0" class="d-flex align-center flex-wrap mt-2">
                        <span class="text-caption grey--text text--darken-2 mr-2">Resumen:</span>
                        <v-chip x-small color="deep-purple lighten-4" class="deep-purple--text font-weight-bold mr-2">
                          Moda: {{ modePreview(story) }}
                        </v-chip>
                        <v-chip x-small :color="pctPreview(story) >= consensusPct ? 'green' : 'orange'" dark class="font-weight-bold">
                          {{ pctPreview(story) }}% de consenso (mín. {{ consensusPct }}%)
                        </v-chip>
                      </div>
                    </v-card>
                  </div>
                </v-card>
              </v-col>
            </v-row>
          </v-container>
        </v-expansion-panel-content>
      </v-expansion-panel>
    </v-expansion-panels>

    <!-- Dialog: Confirmar División de Historia -->
    <v-dialog v-model="splitDialog" max-width="550px" persistent>
      <v-card v-if="storyToSplit" class="rounded-lg">
        <v-card-title class="headline warning lighten-5 warning--text text--darken-4 d-flex align-center">
          <v-icon color="warning darken-2" class="mr-2">mdi-call-split</v-icon>
          Dividir Historia
        </v-card-title>
        <v-card-text class="pa-4">
          <v-alert type="warning" text dense class="mb-3">
            La historia <strong>{{ storyToSplit.UsoCode }} - {{ storyToSplit.UsoTitle }}</strong> es demasiado grande o compleja
            y será dividida en 2 o más historias.
          </v-alert>
          <ul class="body-2 grey--text text--darken-2">
            <li>La historia regresará a <strong>En Refinamiento</strong>.</li>
            <li>Se limpiarán sus votos, talla y consenso.</li>
            <li>Será redirigido a la pantalla de <strong>Refinamiento</strong> para redefinirla y crear las nuevas historias.</li>
          </ul>
        </v-card-text>
        <v-card-actions class="pa-3 grey lighten-4">
          <v-spacer></v-spacer>
          <v-btn text color="grey darken-1" @click="splitDialog = false">Cancelar</v-btn>
          <v-btn color="warning darken-2" dark :loading="splitting" @click="confirmSplit">
            <v-icon left>mdi-call-split</v-icon> Dividir y Refinar
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import _sRequirementService from '@/services/InformationTechnology/TicRequirementService.js';
import _sUserStoryService from '@/services/InformationTechnology/TicUserStoryService.js';
import { statusColor, getScoreColor, pointsMap } from './statusColors.js';

export default {
  name: "Estimation",
  data() {
    return {
      loading: false,
      closingVoting: null,
      splitting: false,
      splitDialog: false,
      storyToSplit: null,
      groups: [],
      votesByStory: {},
      expandedCriteria: {},
      consensusPct: 80,
      voteHeaders: [
        { text: "Desarrollador (Votante)", value: "VoterName", width: "220px" },
        { text: "Talla Seleccionada", value: "VoteSizeName", align: "center", width: "140px" },
        { text: "Fecha Voto", value: "SecCreate", align: "center", width: "140px" }
      ],
      filter: {
        ReqTitle: "",
        ReqType: null,
        ReqStatus: 2 // Requerimientos en refinamiento con historias listas para estimar
      }
    };
  },
  created() {
    this.loadData();
  },
  methods: {
    statusColor(status) {
      return statusColor(status);
    },
    getScoreColor(score) {
      return getScoreColor(score);
    },
    loadData() {
      this.loading = true;
      const currentUserID = Number(this.$fun.getUserID());

      const payload = {
        start: 0,
        length: 300,
        search: { value: "" },
        filter: {
          ReqTitle: this.filter.ReqTitle,
          ReqType: this.filter.ReqType,
          ReqStatus: this.filter.ReqStatus
        }
      };

      _sRequirementService.pagination(payload, currentUserID)
        .then(response => {
          const requirements = (response.data && response.data.data) || [];
          console.log("Estimation: requerimientos recibidos:", requirements.length, requirements);
          this.groups = requirements.map(req => ({ req: req, stories: [] }));

          // Cargar historias de cada requerimiento
          const storyRequests = this.groups.map(group =>
            _sUserStoryService.list({ UsoReqID: group.req.ReqID }, currentUserID)
              .then(res => {
                group.stories = res.data || [];
                console.log(`Estimation: historias del requerimiento ${group.req.ReqID}:`, group.stories.length);
              })
              .catch(() => {
                group.stories = [];
              })
          );

          return Promise.all(storyRequests);
        })
        .then(() => {
          // Cargar votos de todas las historias
          const voteRequests = [];
          this.groups.forEach(group => {
            group.stories.forEach(story => {
              voteRequests.push(this.loadVotes(story));
            });
          });
          return Promise.all(voteRequests);
        })
        .catch(err => {
          console.error("Error loading estimation data:", err);
          this.$fun.alert("Error al cargar la información de estimación", "error");
        })
        .finally(() => {
          this.loading = false;
        });
    },

    loadVotes(story) {
      if (!story || !story.UsoID) return Promise.resolve();
      return _sUserStoryService.getVotes(story.UsoID, Number(this.$fun.getUserID()))
        .then(res => {
          this.$set(this.votesByStory, story.UsoID, res.data || []);
        })
        .catch(() => {
          this.$set(this.votesByStory, story.UsoID, []);
        });
    },

    toggleCriteria(story) {
      this.$set(this.expandedCriteria, story.UsoID, !this.expandedCriteria[story.UsoID]);
    },

    modePreview(story) {
      const votes = this.votesByStory[story.UsoID] || [];
      if (votes.length === 0) return "-";
      const counts = {};
      votes.forEach(v => {
        if (v.VoteSize >= 1 && v.VoteSize <= 5) {
          counts[v.VoteSize] = (counts[v.VoteSize] || 0) + 1;
        }
      });
      let mode = null;
      let maxCount = 0;
      Object.keys(counts).forEach(size => {
        if (counts[size] > maxCount) {
          maxCount = counts[size];
          mode = Number(size);
        }
      });
      if (mode === null) return "-";
      const sizeName = { 1: "XS", 2: "S", 3: "M", 4: "L", 5: "XL" }[mode] || mode;
      return `${sizeName} (${pointsMap[mode]} pts)`;
    },

    pctPreview(story) {
      const votes = this.votesByStory[story.UsoID] || [];
      if (votes.length === 0) return 0;
      const counts = {};
      votes.forEach(v => {
        if (v.VoteSize >= 1 && v.VoteSize <= 5) {
          counts[v.VoteSize] = (counts[v.VoteSize] || 0) + 1;
        }
      });
      const maxCount = Math.max.apply(null, Object.keys(counts).map(k => counts[k]));
      return Math.round((maxCount * 100) / votes.length);
    },

    closeVoting(story) {
      const currentUserID = Number(this.$fun.getUserID());
      this.closingVoting = story.UsoID;

      _sUserStoryService.closeVoting({ UsoID: story.UsoID, UsrUpdateID: currentUserID }, currentUserID)
        .then(response => {
          if (response.status === 200) {
            this.$fun.alert("Votación cerrada por consenso. La historia avanzó a 'Listo para Desarrollo'.", "success");
            this.loadData();
          }
        })
        .catch(error => {
          this.$fun.alert(error?.response?.data?.Message || "No se pudo cerrar la votación", "warning");
        })
        .finally(() => {
          this.closingVoting = null;
        });
    },

    openSplitDialog(story) {
      this.storyToSplit = story;
      this.splitDialog = true;
    },

    confirmSplit() {
      if (!this.storyToSplit) return;
      const currentUserID = Number(this.$fun.getUserID());
      this.splitting = true;

      _sUserStoryService.split({ UsoID: this.storyToSplit.UsoID, UsrUpdateID: currentUserID }, currentUserID)
        .then(response => {
          if (response.status === 200) {
            this.splitDialog = false;
            this.$fun.alert("Historia devuelta a Refinamiento para ser dividida.", "success");
            this.$router.push({
              name: "TIC_REFINEMENT",
              query: { reqID: this.storyToSplit.UsoReqID }
            });
          }
        })
        .catch(error => {
          this.$fun.alert(error?.response?.data?.Message || "No se pudo dividir la historia", "error");
        })
        .finally(() => {
          this.splitting = false;
        });
    }
  }
};
</script>

<style scoped>
.line-clamp-2 {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}
</style>
